import { useEffect } from 'react'

/**
 * Warns the user before navigating away from a page with unsaved changes.
 *
 * Currently only handles browser tab close / refresh via `beforeunload`.
 * In-app navigation guards via react-router's `useBlocker` require a data
 * router (`createBrowserRouter` + `RouterProvider`); this app uses the
 * legacy `<BrowserRouter>`, so in-app blocking is not wired here.
 * Migrating the app to a data router is a separate effort.
 *
 * Returns a stable blocker-shaped no-op so callers can keep their JSX
 * unchanged when the data-router migration lands.
 */
export interface UnsavedChangesBlocker {
  state: 'unblocked' | 'blocked'
  reset: () => void
  proceed: () => void
}

const NOOP_BLOCKER: UnsavedChangesBlocker = {
  state: 'unblocked',
  reset: () => {},
  proceed: () => {},
}

export function useUnsavedChangesWarning(isDirty: boolean): UnsavedChangesBlocker {
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) e.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  return NOOP_BLOCKER
}
